<template>
  <v-sheet
    v-if="isPlacementEnabled"
    class="mx-auto bg-transparent"
    max-width="100%"
    role="region"
    aria-label="Vehicle Listings Carousel"
    v-intersect="onPlacementIntersect"
    :threshold="[0, 0.25]"
    :style="{
      '--card-height': placementSettings.cardSettings.height === '0' 
        ? 'auto' 
        : (placementSettings.cardSettings.height ?? '380') + (placementSettings.cardSettings.heightUnit ?? 'px'),
      '--card-min-height': placementSettings.cardSettings.height === '0' 
        ? '100%' 
        : (placementSettings.cardSettings.height ?? '380') + (placementSettings.cardSettings.heightUnit ?? 'px')
    }"
  >
    <div v-if="layout === 'grid'" class="d-flex flex-wrap layout-grid">
      <div
        v-for="(item, idx) in loading ? skeletonItems : filteredItems"
        :key="typeof item === 'number' ? idx : item.fields.vin"
        class="micro-srp__item"
        :style="{
          margin:
            (placementSettings.cardSettings.margin ?? '4') +
            (placementSettings.cardSettings.marginUnit ?? 'px'),
        }"
      >
        <v-card
          v-if="typeof item !== 'number'"
          @click="handleUserAction(item, $event)"
          class="mb-1 micro-srp__card"
          :style="{
            cursor: 'pointer',
            ...cardBorderRadius,
            ...responsiveCard,
            paddingBottom:
              placementSettings.cardSettings.height === '0' ? '50px' : '0',
            minHeight:
              placementSettings.cardSettings.height === '0' ? '100%' : '0',
            display: 'flex',
            flexDirection: 'column',
          }"
          v-intersect="(isVisible: boolean) => onIntersect(isVisible, item)"
          :threshold="[0, 0.5, 1]"
          role="article"
        >
          <v-img
            :src="item?.image"
            :alt="`${item.fields.conditions ?? ''} ${item.fields.year ?? ''} ${item.fields.make ?? ''} ${item.fields.model ?? ''} ${item.fields.trim ?? ''}`"
            contain
            class="bg-grey-lighten-2"
            @error="handleImageError"
            :style="{ ...cardImageDimensions }"
          >
            <template v-slot:placeholder>
              <v-row
                class="fill-height ma-0"
                align="center"
                justify="center"
                role="status"
                aria-live="polite"
                aria-label="Loading image placeholder"
              >
                <div
                  v-if="imageTimeout"
                  class="text-subtitle-1 text-center"
                  style="width: 100%"
                >
                  <v-img
                    :src="placeholderImage"
                    style="
                      max-width: 100%;
                      max-height: 100%;
                      object-fit: contain;
                    "
                    alt="No Image Available"
                  ></v-img>
                </div>
                <v-progress-circular
                  v-else
                  indeterminate
                  color="grey-lighten-5"
                  aria-label="Loading indicator"
                />
              </v-row>
            </template>
          </v-img>
          <v-card-text class="micro-srp-info">
            <div class="details-container">
              <div
                v-for="(info, index) in visibleVehicleDetailRows"
                :key="index"
              >
                <div :style="getStyles(info)" class="details-row">
                  {{ processTemplate(info.template, item.fields) }}
                </div>
              </div>
            </div>
          </v-card-text>
          <v-card-actions class="micro-srp-actions">
            <v-spacer></v-spacer>
            <v-btn
              :href="item.link"
              @click.stop="handleUserAction(item, $event)"
              class="micro-srp__cta"
              role="button"
              :aria-label="`Go to details for ${item.fields.year} ${item.fields.make} ${item.fields.model} ${item.fields.trim}`"
            >
              <span>{{ placementSettings.callToAction.label }}</span>
              <v-icon
                :icon="'svg:M8.59,16.58L13.17,12L8.59,7.41L10,6L16,12L10,18L8.59,16.58Z'"
              ></v-icon>
            </v-btn>
          </v-card-actions>
        </v-card>
        <v-card
          v-else
          class="mb-1 micro-srp__card"
          :style="{
            width: '100%',
            ...cardBorderRadius,
            ...responsiveCard,
            paddingBottom:
              placementSettings.cardSettings.height === '0' ? '50px' : '0',
            minHeight:
              placementSettings.cardSettings.height === '0' ? '100%' : '0',
            display: 'flex',
            flexDirection: 'column',
          }"
          role="status"
          aria-label="Loading skeleton card"
        >
          <!-- Image skeleton -->
          <v-skeleton-loader type="image" />
          
          <!-- Content skeleton matching actual structure -->
          <v-card-text class="micro-srp-info">
            <div class="details-container">
              <!-- Generate skeleton rows based on visible vehicle detail rows -->
              <div
                v-for="(info, index) in visibleVehicleDetailRows"
                :key="`skeleton-${index}`"
                class="details-row"
              >
                <v-skeleton-loader type="text" />
              </div>
            </div>
          </v-card-text>
          
          <!-- Actions skeleton matching actual structure -->
          <v-card-actions class="micro-srp-actions">
            <v-spacer></v-spacer>
            <v-skeleton-loader type="button" />
          </v-card-actions>
        </v-card>
      </div>
    </div>
    <v-slide-group
      v-else
      :show-arrows="!isMobile"
      :hide-delimiters="isMobile"
      class="justify-center"
      v-model="selected"
      :mandatory="false"
      ref="slideGroup"
    >
      <template v-slot:prev="">
        <v-icon
          :icon="'svg:M15.41,16.58L10.83,12L15.41,7.41L14,6L8,12L14,18L15.41,16.58Z'"
        ></v-icon>
      </template>

      <template v-slot:next="">
        <v-icon
          :icon="'svg:M8.59,16.58L13.17,12L8.59,7.41L10,6L16,12L10,18L8.59,16.58Z'"
        ></v-icon>
      </template>

      <v-slide-group-item
        v-for="(item, idx) in loading ? skeletonItems : filteredItems"
        :key="typeof item === 'number' ? idx : item.fields.vin"
        :value="idx"
      >
        <v-card
          class="mb-1 micro-srp__card"
          :style="{
            cursor: !loading ? 'pointer' : 'default',
            ...cardDimensions,
            ...cardBorderRadius,
            ...responsiveCard,
            paddingBottom:
              placementSettings.cardSettings.height === '0' ? '50px' : '0',
            minHeight:
              placementSettings.cardSettings.height === '0' ? '100%' : '0',
            display: 'flex',
            flexDirection: 'column',
          }"
          v-intersect="(isVisible: boolean) => onCardIntersect(isVisible, item)"
          :threshold="[0, 0.5, 1]"
          role="article"
          :aria-busy="loading ? 'true' : 'false'"
        >
          <!-- Image area -->
          <template v-if="loading">
            <v-skeleton-loader type="image" />
          </template>
          <v-img
            v-else
            :src="`${(item as any)?.image}`"
            :alt="`${(item as any).fields.conditions ?? ''} ${(item as any).fields.year ?? ''} ${(item as any).fields.make ?? ''} ${(item as any).fields.model ?? ''} ${(item as any).fields.trim ?? ''}`"
            contain
            class="bg-grey-lighten-2"
            @error="handleImageError"
            :style="{ ...cardImageDimensions }"
          >
            <template v-slot:placeholder>
              <v-row
                class="fill-height ma-0"
                align="center"
                justify="center"
                role="status"
                aria-live="polite"
                aria-label="Loading image placeholder"
              >
                <div
                  v-if="imageTimeout"
                  class="text-subtitle-1 text-center"
                  style="width: 100%"
                >
                  <v-img
                    :src="placeholderImage"
                    style="
                      max-width: 100%;
                      max-height: 100%;
                      object-fit: contain;
                    "
                    alt="No Image Available"
                  ></v-img>
                </div>
                <v-progress-circular
                  v-else
                  indeterminate
                  color="grey-lighten-5"
                  aria-label="Loading indicator"
                />
              </v-row>
            </template>
          </v-img>

          <!-- Content area -->
          <v-card-text class="micro-srp-info">
            <div class="details-container">
              <template v-if="loading">
                <div
                  v-for="(_, index) in visibleVehicleDetailRows"
                  :key="`skeleton-${index}`"
                  class="details-row"
                >
                  <v-skeleton-loader type="text" />
                </div>
              </template>
              <template v-else>
                <div
                  v-for="(info, index) in visibleVehicleDetailRows"
                  :key="index"
                >
                  <div :style="getStyles(info)" class="details-row">
                    {{ processTemplate(info.template, (item as any).fields) }}
                  </div>
                </div>
              </template>
            </div>
          </v-card-text>

          <!-- Actions area -->
          <v-card-actions class="micro-srp-actions">
            <v-spacer></v-spacer>
            <template v-if="loading">
              <v-skeleton-loader type="button" />
            </template>
            <template v-else>
              <v-btn
                :href="(item as any).link"
                @click.stop="handleUserAction(item as any, $event)"
                class="micro-srp__cta"
                role="button"
                :aria-label="`Go to details for ${(item as any).fields.year} ${(item as any).fields.make} ${(item as any).fields.model} ${(item as any).fields.trim}`"
              >
                <span>{{ placementSettings.callToAction.label }}</span>
                <v-icon
                  :icon="'svg:M8.59,16.58L13.17,12L8.59,7.41L10,6L16,12L10,18L8.59,16.58Z'"
                ></v-icon>
              </v-btn>
            </template>
          </v-card-actions>
        </v-card>
      </v-slide-group-item>
    </v-slide-group>
  </v-sheet>
</template>

<script setup lang="ts">
import { useSettingsStore } from '@/stores/settings'
import type {
  IItem,
  IPlacementSettings,
  IVehicleDetailRow,
  ICallToAction,
  IColorSettings,
  ITypography,
} from '@/types'
import { processTemplate } from '@/utils'
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useDisplay } from 'vuetify'
import { initializeTracking } from '@/utils/tracking'
import placeholderImage from '@/assets/no-vehicle-image.png'
import { VSlideGroup } from 'vuetify/components'
import { config } from '@/config/generated-config'

interface Props {
  items: IItem[]
  loading: boolean
}

const props = withDefaults(defineProps<Props>(), {
  items: () => [],
  loading: false,
})

const settings = useSettingsStore()
const placementSettings: IPlacementSettings = settings.placementSettings
const imageServiceApi = config.VITE_IMAGE_SERVICE_API
const isPlacementEnabled = computed(() => {
  return placementSettings?.enabled ?? true
})

const skeletonItems = Array.from(
  { length: placementSettings?.numberOfVehicles ?? 4 },
  (_, i) => i,
)
const layout = placementSettings?.layout?.type ?? 'slider'

const imageError = ref(false)
const imageTimeout = ref(false)
const imageLoadTimeout = 30000

const { xs } = useDisplay()
const isMobile = computed(() => xs.value)

const filteredItems = computed(() =>
  props.items.map(item => {
    const visibleUrl = item.fields?.visual?.image?.visibleUrl
    const imageId = typeof visibleUrl === 'string' ? visibleUrl.match(/\d+/g)?.[0] : undefined

    if (!imageId) {
      item.image = placeholderImage
    } else {
      item.image = `${imageServiceApi}/media/${imageId}?scale=450/-1/70`
    }

    return item
  }),
)

const visibleVehicleDetailRows = computed(() => {
  return (
    placementSettings?.vehicleDetailRows?.filter(
      (row: IVehicleDetailRow) => row.visible !== false,
    ) || []
  )
})

const getImageUrlFromCdn = (imagePath: string) => {
  const cdn = config.VITE_IMAGE_SERVICE_API
  return `${cdn}/${imagePath}?scale=450/-1/70`
}

const handleImageError = () => {
  imageError.value = true
}

const selected = ref(null)
const slideGroup = ref<InstanceType<typeof VSlideGroup> | null>(null)

const placementViewTracked = ref(false)

const preventAutoFocus = (event: FocusEvent) => {
  // Prevent default focus behavior when nothing is selected
  if (selected.value === null) {
    event.stopPropagation()
    event.preventDefault()
  }
}

onMounted(() => {
  nextTick(() => {
    const el = slideGroup.value?.$el as HTMLElement | undefined
    if (el) {
      el.addEventListener('focusin', preventAutoFocus, true)
    }
  })
  setTimeout(() => {
    imageTimeout.value = true
  }, imageLoadTimeout)
})

onBeforeUnmount(() => {
  const el = slideGroup.value?.$el as HTMLElement | undefined
  if (el) {
    el.removeEventListener('focusin', preventAutoFocus, true)
  }
})

watch(filteredItems, items => {
  if (items.length > 0) {
    for (const item of items) {
      injectSchema(getSchema(item))
    }
  }
})

const injectSchema = (schema: any) => {
  const script = document.createElement('script')
  script.type = 'application/ld+json'
  script.textContent = JSON.stringify(schema)
  document.head.appendChild(script)
}

const getSchema = (item: IItem) => {
  const fields = item?.fields ?? {}
  const vin = fields.vin ?? ''
  const year = fields.year ?? ''
  const make = fields.make ?? ''
  const model = fields.model ?? ''
  const trim = fields.trim ?? ''
  const styleName = fields.style_name ?? ''
  const condition = fields.used ? 'Used' : 'New'
  const price = Math.abs(fields.price ?? 0)
  const vehicleType = ''
  const passengerCapacity = fields.passenger_capacity ?? 0
  const drivetrain = fields.drivetrain ?? ''
  const mileage = fields.mileage ?? 0
  const exteriorColor = fields.exterior_color ?? ''
  const interiorFactoryColor = fields.interior_factory_color ?? ''
  const fuelType = fields.fuel_type ?? ''
  const transmission = fields.transmission ?? ''

  return {
    '@context': 'https://schema.org',
    '@type': 'Car',
    name: `${year} ${make} ${model}`,
    image: item.image,
    'bodyType:': vehicleType,
    vehicleIdentificationNumber: vin,
    offers: {
      '@type': 'Offer',
      availability: 'https://schema.org/InStock',
      price: price,
      priceCurrency: 'USD',
    },
    itemCondition: `https://schema.org/${condition}Condition`,
    brand: {
      '@type': 'Brand',
      name: make,
    },
    model: model,
    vehicleConfiguration: trim ?? styleName,
    vehicleSeatingCapacity: passengerCapacity,
    driveWheelConfiguration: `https://schema.org/${drivetrain.replace(/\s/g, '')}Configuration`,
    vehicleModelDate: year,
    mileageFromOdometer: {
      '@type': 'QuantitativeValue',
      value: mileage,
      unitCode: 'SMI',
    },
    color: exteriorColor,
    vehicleInteriorColor: interiorFactoryColor,
    vehicleInteriorType: 'Standard',
    vehicleEngine: {
      '@type': 'EngineSpecification',
      fuelType: fuelType,
    },
    vehicleTransmission: transmission,
  }
}

const getStyles = (data: IVehicleDetailRow): any => {
  const typography = data.typography ?? {}
  const colors: IColorSettings = data.colors ?? {}
  const fontFamily = typography.fontFamily ?? 'inherit'
  const fontStyle = typography.fontStyle ?? 'normal'
  const fontWeight = typography.fontWeight ?? 'normal'
  const textDecoration = typography.textDecoration ?? 'none'
  const textAlignment = typography.textAlignment ?? 'left'
  const textTransform = typography.textTransform ?? 'none'

  let fontSize = 'inherit'
  if (typography?.fontSize != null && typography?.fontSizeUnit != null) {
    fontSize = `${typography?.fontSize ?? ''}${typography?.fontSizeUnit ?? ''}`
  }

  let letterSpacing = 'inherit'
  if (
    typography?.letterSpacing != null &&
    typography?.letterSpacingUnit != null
  ) {
    letterSpacing = `${typography?.letterSpacing ?? ''}${typography?.letterSpacingUnit ?? ''}`
  }
  let lineHeight = 'inherit'
  if (typography?.lineHeight != null && typography?.lineHeightUnit != null) {
    lineHeight = `${typography?.lineHeight ?? ''}${typography?.lineHeightUnit ?? ''}`
  }

  return {
    backgroundColor: colors?.background ?? 'inherit',
    '--hover-background-color': colors?.backgroundHover ?? 'inherit',
    color: colors?.color ?? 'inherit',
    '--hover-color': colors?.colorHover ?? 'inherit',
    fontFamily,
    fontSize,
    fontStyle,
    fontWeight,
    letterSpacing,
    lineHeight,
    textAlign: textAlignment,
    textDecoration,
    textTransform,
  }
}

const callToAction = computed(() => placementSettings.callToAction ?? {})
const ctaTypography = computed(() => callToAction.value?.typography ?? ({} as ITypography))
const ctaBorder = computed(() => callToAction.value?.border ?? {})
const ctaIcon = computed(() => callToAction.value?.icon ?? {})

const ctaFontSize = computed(
  () => `${ctaTypography.value?.fontSize ?? ''}${ctaTypography.value?.fontSizeUnit ?? ''}`,
)
const ctaLetterSpacing = computed(
  () =>
    `${ctaTypography.value?.letterSpacing ?? ''}${ctaTypography.value?.letterSpacingUnit ?? ''}`,
)
const ctaLineHeight = computed(
  () =>
    `${ctaTypography.value?.lineHeight ?? ''}${ctaTypography.value?.lineHeightUnit ?? ''}`,
)
const ctaIconContainerSize = computed(
  () =>
    `${ctaIcon.value?.sizes?.container ?? '30'}${ctaIcon.value?.sizes?.containerUnit ?? 'px'}`,
)
const ctaIconSize = computed(
  () => `${ctaIcon.value?.sizes?.icon ?? '30'}${ctaIcon.value?.sizes?.iconUnit ?? 'px'}`,
)
const ctaBorderStyle = computed(
  () =>
    `${ctaBorder.value?.width ?? '1'}${ctaBorder.value?.widthUnit ?? 'px'} ${ctaBorder.value?.style ?? 'solid'} ${ctaBorder.value?.color ?? 'inherit'}`,
)
const ctaBorderHoverStyle = computed(
  () =>
    `${ctaBorder.value?.width ?? '1'}${ctaBorder.value?.widthUnit ?? 'px'} ${ctaBorder.value?.style ?? 'solid'} ${ctaBorder.value?.hoverColor ?? 'inherit'}`,
)
const ctaBorderRadius = computed(
  () => `${ctaBorder.value?.radius ?? '0'}${ctaBorder.value?.radiusUnit ?? 'px'}`,
)
const sliderArrowSize = computed(() => {
  const fontSize = placementSettings?.sliderArrowSettings?.fontSize ?? '48'
  const fontSizeUnit =
    placementSettings?.sliderArrowSettings?.fontSizeUnit ?? 'px'
  return `${fontSize}${fontSizeUnit}`
})
const sliderArrowColor = computed(
  () => placementSettings?.sliderArrowSettings?.colors?.color ?? 'initial',
)
const sliderArrowColorHover = computed(
  () => placementSettings?.sliderArrowSettings?.colors?.colorHover ?? 'initial',
)

const tracker = initializeTracking(settings.accountId, settings.placementId)

const onIntersect = (isVisible: boolean, item: IItem) => {
  if (isVisible) {
    const accountId = settings.accountId
    const placement = settings.placementId
    const vin = item?.fields?.vin ?? ''
    tracker.trackEvent(accountId, vin, 'vehicle_view', placement, {
      vin: vin,
      stockNumber: item.fields.stockNumber,
      make: item.fields.make,
      model: item.fields.model,
      year: item.fields.year,
      trim: item.fields.trim,
      price:
        item.fields.displayPrice ??
        item.fields.display_price ??
        item.fields.price,
    })
  }
}

// Wrapper for unified slider card branch: ignore intersections while loading
// or when the loop item is a skeleton index (number).
const onCardIntersect = (isVisible: boolean, maybeItem: unknown) => {
  if (props.loading || typeof maybeItem === 'number') {
    return
  }
  onIntersect(isVisible, maybeItem as IItem)
}

const onPlacementIntersect = (isVisible: boolean) => {
  if (placementViewTracked.value || !isVisible) {
    return
  }
  const accountId = settings.accountId
  const placement = settings.placementId
  tracker.trackEvent(accountId, placement, 'placement_view', placement)
  placementViewTracked.value = true
}

const handleUserAction = (item: IItem, event?: Event) => {
  // Prevent default navigation if event is provided
  if (event) {
    event.preventDefault()
  }

  const accountId = settings.accountId
  const placement = settings.placementId
  const vin = item?.fields?.vin ?? ''

  // Track the event
  tracker.trackEvent(accountId, vin, 'vehicle_click', placement, {
    vin: vin,
    stockNumber: item.fields.stockNumber,
    make: item.fields.make,
    model: item.fields.model,
    year: item.fields.year,
    trim: item.fields.trim,
    price:
      item.fields.displayPrice ??
      item.fields.display_price ??
      item.fields.price,
  })

  // Navigate after a short delay to ensure tracking request is sent
  setTimeout(() => {
    if (item.link) {
      window.location.href = item.link
    }
  }, 500) // 500ms delay to ensure tracking request is sent
}

const cardDimensions = computed(() => ({
  minWidth: `${placementSettings.cardSettings.width ?? '340'}${placementSettings.cardSettings.widthUnit ?? 'px'}`,
  width: `${placementSettings.cardSettings.width ?? '340'}${placementSettings.cardSettings.widthUnit ?? 'px'}`,
}))

const cardImageDimensions = computed(() => ({
  maxHeight: `${placementSettings.cardSettings.imageHeight ?? '200'}${placementSettings.cardSettings.imageHeightUnit ?? 'px'}`,
}))

const cardBorderRadius = computed(() => ({
  borderRadius: `${placementSettings.cardSettings.borderRadius ?? '0'}${placementSettings.cardSettings.borderRadiusUnit ?? 'px'}`,
}))

const responsiveCard = computed(() => ({
  ...(isMobile.value
    ? { height: 'auto', width: cardWidth.value, flex: `0 0 ${cardWidth.value}` }
    : {}),
}))

const cardWidth = computed((): string => {
  // On mobile, always use 100% width for grid layout
  if (window.innerWidth <= 767 && layout === 'grid') {
    return '100%'
  }

  // Use the actual card width setting instead of calculating from cards per row
  const width = placementSettings?.cardSettings?.width ?? '300'
  const unit = placementSettings?.cardSettings?.widthUnit ?? 'px'
  return `${width}${unit}`
})

const cardSpacingDoubled = computed((): string => {
  const margin: number =
    Number(placementSettings?.cardSettings?.margin ?? 4) * 2
  const marginUnit = placementSettings?.cardSettings?.marginUnit ?? 'px'

  return `${margin}${marginUnit}`
})

const cardSpacing = computed((): string => {
  const margin = placementSettings?.cardSettings?.margin ?? 4
  const marginUnit = placementSettings?.cardSettings?.marginUnit ?? 'px'

  return `${margin}${marginUnit}`
})

const cardGap = computed(
  () =>
    `${placementSettings?.cardSettings?.margin ?? '4'}${placementSettings?.cardSettings?.marginUnit ?? 'px'}`,
)
</script>

<style scoped>
.micro-srp-info {
  padding: 8px 10px;
  position: relative;
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
  max-height: 140px !important; /* Fixed height for text area */
  height: 140px !important; /* Force height */
}

.details-container {
  flex: 1;
  overflow-y: auto !important;
  overflow-x: hidden;
  scrollbar-width: thin;
  scrollbar-color: rgba(0, 0, 0, 0.3) transparent;
  min-height: 0;
  max-height: 120px !important; /* Fixed height for text area */
  height: 120px !important; /* Force height */
}

.details-container::-webkit-scrollbar {
  width: 4px;
}

.details-container::-webkit-scrollbar-track {
  background: transparent;
}

.details-container::-webkit-scrollbar-thumb {
  background-color: rgba(0, 0, 0, 0.3);
  border-radius: 2px;
}

.micro-srp-actions {
  min-height: unset;
  padding: 0;
  position: relative;
  z-index: 1;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  margin-top: auto;
  padding-right: 8px;
  flex-shrink: 0;
}

.micro-srp :deep(.micro-srp__card) {
  box-shadow:
    rgba(0, 0, 0, 0.2) 0 2px 1px -1px,
    rgba(0, 0, 0, 0.14) 0 1px 1px 0,
    rgba(0, 0, 0, 0.12) 0 1px 3px 0;
  position: relative;
}

/* Force image height constraints - use max-height to prevent expansion */
.micro-srp :deep(.v-img) {
  max-height: v-bind('cardImageDimensions.maxHeight') !important;
  overflow: hidden !important;
  width: 100% !important;
}

.micro-srp :deep(.v-img_img) {
  height: 100% !important;
  max-height: 100% !important;
  width: 100% !important;
  object-fit: contain !important;
  object-position: center !important;
}

.micro-srp :deep(.v-responsive) {
  max-height: v-bind('cardImageDimensions.maxHeight') !important;
  width: 100% !important;
  overflow: hidden !important;
}

/* Additional constraints for Vuetify components */
.micro-srp :deep(.v-responsive__sizer) {
  max-height: v-bind('cardImageDimensions.maxHeight') !important;
}

.micro-srp :deep(.v-responsive__content) {
  max-height: v-bind('cardImageDimensions.maxHeight') !important;
}

/* Override any Vuetify height calculations with max-height */
.micro-srp :deep([style*='height']) {
  max-height: v-bind('cardImageDimensions.maxHeight') !important;
}

.micro-srp__card :deep(.v-card__overlay) {
  transition: none;
}

.micro-srp__cta {
  background-color: v-bind('callToAction?.colors?.background ?? "inherit"');
  border: v-bind('ctaBorderStyle');
  border-radius: v-bind('ctaBorderRadius');
  color: v-bind('callToAction?.colors?.color ?? "inherit"');
  height: auto;
  padding: 0 0 0 6px;
  position: relative;
  margin: 8px 0;
  overflow: hidden;
  flex-shrink: 0;
}
.micro-srp__cta:hover {
  border: v-bind('ctaBorderHoverStyle');
}
.micro-srp__cta :deep(.v-btn__content span) {
  font-family: v-bind('callToAction?.typography?.fontFamily ?? "inherit"');
  font-size: v-bind('ctaFontSize');
  font-style: v-bind('callToAction?.typography?.fontStyle ?? "normal"');
  font-weight: v-bind('callToAction?.typography?.fontWeight ?? "normal"');
  letter-spacing: v-bind('ctaLetterSpacing');
  text-decoration: v-bind('callToAction?.typography?.textDecoration ?? "none"');
  text-transform: v-bind('callToAction?.typography?.textTransform ?? "none"');
  line-height: v-bind('ctaLineHeight');

  transition-property: all;
  transition-duration: 0.28s;
  transition-delay: unset;
  transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
}
.micro-srp__cta:hover {
  background-color: v-bind(
    'callToAction?.colors?.backgroundHover ?? "inherit"'
  );
  color: v-bind('callToAction?.colors?.colorHover ?? "inherit"');
}
.micro-srp__cta:hover :deep(.v-icon) {
  background-color: v-bind(
    'callToAction?.icon?.colors?.backgroundHover ?? "inherit"'
  );
  color: v-bind('callToAction?.icon?.colors?.colorHover ?? "inherit"');
}
.micro-srp__cta :deep(.v-icon) {
  background-color: v-bind(
    'callToAction?.icon?.colors?.background ?? "inherit"'
  );
  color: v-bind('callToAction?.icon?.colors?.color ?? "inherit"');
  height: v-bind('ctaIconContainerSize');
  margin-left: 6px;
  width: v-bind('ctaIconContainerSize');

  transition-property: all;
  transition-duration: 0.28s;
  transition-delay: unset;
  transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
}
.micro-srp__cta :deep(.v-icon):before {
  font-size: v-bind('ctaIconSize');
}

:deep(.v-slide-group__prev),
:deep(.v-slide-group__next) {
  color: v-bind('sliderArrowColor');
  min-width: 48px !important;
}

:deep(.v-slide-group__prev):hover,
:deep(.v-slide-group__next):hover {
  color: v-bind('sliderArrowColorHover');
}

:deep(.v-slide-group__prev) i,
:deep(.v-slide-group__next) i {
  font-size: v-bind('sliderArrowSize');
}

:deep(.v-slide-group__content) {
  display: flex !important;
  scroll-snap-type: x mandatory;
  scroll-behavior: smooth;
  -webkit-overflow-scrolling: touch;
  overflow: visible;
  gap: v-bind('cardGap') !important;
  padding: 6px 0;
}

/* Prevent first/last card shadows from being clipped */
:deep(.v-slide-group__container) {
  padding-left: 4px;
  padding-right: 4px;
}

:deep(.v-slide-group-item) {
  scroll-snap-align: start;
  scroll-snap-stop: always;
}

.custom-square-btn {
  border-radius: 4px;
}

.micro-srp-info .details-row {
  line-height: normal;
}

.micro-srp-info .details-row:hover {
  background-color: var(--hover-background-color) !important;
  color: var(--hover-color) !important;
}

/* Handle empty details container */
.details-container:empty::after {
  content: '';
  display: block;
  min-height: 20px;
}

/* Ensure proper card height constraints */
.micro-srp__card {
  height: var(--card-height, auto) !important;
  min-height: var(--card-min-height, 380px) !important;
}

/* Force text area to be scrollable when content overflows */
.details-container {
  scrollbar-width: thin;
  scrollbar-color: rgba(0, 0, 0, 0.3) transparent;
}

.details-container::-webkit-scrollbar {
  width: 4px;
}

.details-container::-webkit-scrollbar-track {
  background: transparent;
}

.details-container::-webkit-scrollbar-thumb {
  background-color: rgba(0, 0, 0, 0.3);
  border-radius: 2px;
}

.layout-grid .micro-srp__item {
  margin: v-bind('cardSpacing');
  width: calc(v-bind('cardWidth') - v-bind('cardSpacingDoubled'));
}

/* Match slider: prevent edge clipping in grid layout */
.layout-grid {
  padding-left: 4px;
  padding-right: 4px;
}

@media all and (max-width: 600px) {
  :deep(.micro-srp__card) {
    max-width: 90%;
  }
}
@media all and (max-width: 767px) {
  :deep(.v-slide-group__content) {
    width: 100%;
  }
  
  /* Ensure all cards have 100% max-width on mobile */
  :deep(.micro-srp__card) {
    max-width: 100% !important;
    height: var(--card-height, auto);
  }
  
  .layout-grid .micro-srp__item,
  .layout-grid .micro-srp__card {
    max-width: 100%;
    width: 100% !important;
  }

  /* Ensure proper flexbox behavior on mobile */
  .micro-srp-info {
    flex: 1;
    min-height: 0;
    max-height: none !important;
    height: auto !important;
  }

  .details-container {
    min-height: 0;
    max-height: none !important;
    height: auto !important;
  }

  .micro-srp-actions {
    margin-top: 8px;
  }

  /* On mobile, allow cards to expand naturally */
  .micro-srp__card {
    height: var(--card-height, auto) !important;
    min-height: var(--card-min-height, auto) !important;
  }
}
</style>
